import React from 'react';

const TermsPage: React.FC = () => {
  return (
    <div className="pt-20 bg-primary-black min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <h1 className="text-4xl font-bold text-primary-gold mb-8 font-display">Terms of Service</h1>
        <p className="text-gray-400 mb-8">Last Updated: March 2026</p>

        <div className="space-y-8 text-gray-300">
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 font-display">1. Introduction</h2>
            <p>
              Welcome to The Creamery ("we," "our," "us"). These Terms of Service ("Terms") govern your use of our 
              website and services, including online reservations and dining at our premises.
            </p>
            <p className="mt-4">
              By using our website, you agree to these Terms. If you do not agree, please do not use our website.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 font-display">2. Use of Our Website</h2>
            <p className="mb-4">
              You agree to use our website in accordance with all applicable laws and regulations of England and Wales. 
              You must not:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Use the website for fraudulent or unlawful purposes</li>
              <li>Interfere with the operation or security of the website</li>
              <li>Attempt to gain unauthorised access to systems or data</li>
            </ul>
            <p className="mt-4">We reserve the right to suspend or restrict access for misuse.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 font-display">3. Reservations and Bookings</h2>
            
            <h3 className="text-xl font-semibold text-primary-gold mb-3">a. Booking Policy</h3>
            <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
              <li>Reservations may be made online or by phone.</li>
              <li>You must provide accurate and complete information when booking.</li>
              <li>A booking is not guaranteed until confirmed by us.</li>
            </ul>

            <h3 className="text-xl font-semibold text-primary-gold mb-3">b. Cancellations</h3>
            <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
              <li>We ask that you provide reasonable notice if you need to cancel or amend your reservation.</li>
              <li>For bookings requiring a deposit, cancellation terms will be provided at the time of booking.</li>
            </ul>

            <h3 className="text-xl font-semibold text-primary-gold mb-3">c. No-Shows and Late Arrivals</h3>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Tables may be held for a limited time (e.g., 10–15 minutes).</li>
              <li>We reserve the right to cancel your booking if you arrive late without notice.</li>
              <li>Repeated no-shows may result in refusal of future bookings.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 font-display">4. Deposits and Payments</h2>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>We may require deposits for large group bookings, events, or peak times.</li>
              <li>Deposit terms (including refundability) will be clearly communicated before confirmation.</li>
              <li>In line with the Consumer Rights Act 2015, any charges will be fair, transparent, and proportionate.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 font-display">5. Pricing and Service</h2>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>All prices are displayed in GBP (£) and include VAT where applicable.</li>
              <li>We reserve the right to change prices at any time, but this will not affect confirmed bookings.</li>
              <li>An optional service charge may be added to your bill; this will be clearly stated.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 font-display">6. Food Safety and Allergies</h2>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Please inform staff of any allergies or dietary requirements before ordering.</li>
              <li>
                While we follow UK food safety standards, including guidance from the Food Standards Agency, 
                we cannot guarantee that any dish is completely allergen-free.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 font-display">7. Alcohol Service</h2>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>We operate in accordance with UK licensing laws, including the Licensing Act 2003.</li>
              <li>We may request valid photo ID and refuse alcohol service where required by law.</li>
              <li>We reserve the right to refuse service to anyone who appears intoxicated or behaves inappropriately.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 font-display">8. Conduct on Premises</h2>
            <p className="mb-4">
              Guests must behave respectfully towards staff and other customers. We reserve the right to:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Refuse entry or service</li>
              <li>Ask customers to leave the premises where behaviour is abusive, unsafe, or disruptive.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 font-display">9. Events and Live Sports</h2>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>We may show live sports and host special events.</li>
              <li>We do not guarantee specific matches, commentary, or seating unless explicitly confirmed.</li>
              <li>Event bookings may be subject to additional terms.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 font-display">10. Intellectual Property</h2>
            <p>
              All content on our website is owned by or licensed to us and protected under UK intellectual property laws. 
              You may not copy or reuse content without permission.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 font-display">11. Limitation of Liability</h2>
            <p className="mb-4">
              Nothing in these Terms excludes or limits liability where it would be unlawful to do so, including liability for:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
              <li>Death or personal injury caused by negligence</li>
              <li>Fraud or fraudulent misrepresentation</li>
            </ul>
            <p className="mb-2">Subject to the above:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>We are not liable for indirect or consequential losses</li>
              <li>We do not guarantee uninterrupted website availability</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 font-display">12. Privacy</h2>
            <p>
              Your personal data is handled in accordance with our Privacy Policy and applicable UK data protection laws, 
              including the UK GDPR and Data Protection Act 2018.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 font-display">13. Changes to These Terms</h2>
            <p>
              We may update these Terms from time to time. Changes will be posted on this page with a revised "Last Updated" date.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 font-display">14. Governing Law</h2>
            <p>
              These Terms are governed by the laws of England and Wales. Any disputes will be subject to the exclusive 
              jurisdiction of the courts of England and Wales.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 font-display">15. Contact Us</h2>
            <p className="mb-4">If you have any questions about these Terms, please contact us:</p>
            <div className="bg-gray-900 border border-primary-gold rounded-lg p-6">
              <p className="font-semibold text-white">The Creamery</p>
              <p>Bainsford Main Street</p>
              <p>Falkirk, FK2 7NU</p>
              <p className="mt-4">Email: enquiries@thecreamerysportsbar.co.uk</p>
              <p>Telephone: 01324 636330</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
